import React, { Children, Fragment, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Icon as IconButton } from "@churchofjesuschrist/eden-buttons";
import { GridView, Clear, ArrowBack, ChevronLeft, ChevronRight } from "@churchofjesuschrist/eden-icons";
import { spacing64, breakWidth480, spacing8 } from "@churchofjesuschrist/eden-style-constants";
import { dir } from "@churchofjesuschrist/eden-styled-helpers";
import { Text4 } from "@churchofjesuschrist/eden-text";
import HotKey from "@churchofjesuschrist/eden-hotkey";
import Mask from "@churchofjesuschrist/eden-platform-mask";
import Caption from "./Caption";
import Grid from "./Grid";
import ItemLayout from "./ItemLayout";
import Head from "./Head";
import { AppearanceInvert } from "@churchofjesuschrist/eden-appearance";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Container = styled.div.withConfig({
  componentId: "sc-1xrexcs-0"
})(["display:grid;grid-template-rows:", " 1fr;grid-template-columns:1fr;width:100%;height:100%;overflow:auto;"], spacing64);
const NavButton = styled(IconButton).withConfig({
  componentId: "sc-1xrexcs-1"
})(["", ""], dir("rtl")`
    transform: rotate(180deg);
  `);
const HeadPiece = styled.div.withConfig({
  componentId: "sc-1xrexcs-2"
})(["display:flex;align-items:center;"]);
const HeadNav = styled.div.withConfig({
  componentId: "sc-1xrexcs-3"
})(["display:flex;@media (min-width:", "){display:none;}"], breakWidth480);
const MediaModal = _ref => {
  let {
    onClose,
    children,
    initialItemIndex = 0,
    closeKey = "Escape",
    gridViewLabel = "Grid View",
    backLabel = "Back",
    previousLabel = "Previous",
    nextLabel = "Next",
    closeLabel = "Close",
    ...props
  } = _ref;
  const items = Children.toArray(children);
  const [viewMode, setViewMode] = useState("item");
  const [currentItemIndex, setCurrentItemIndex] = useState(initialItemIndex);
  const goToGrid = () => {
    setViewMode("grid");
  };
  const goToItem = () => {
    setViewMode("item");
  };
  const handleItemSelected = index => {
    setCurrentItemIndex(index);
    setViewMode("item");
  };
  const handleClose = () => {
    onClose();
  };
  const handlePrevious = () => {
    const itemsCount = items.length;
    if (currentItemIndex === 0) {
      setCurrentItemIndex(itemsCount - 1);
    } else {
      setCurrentItemIndex(prevIndex => prevIndex - 1);
    }
  };
  const handleNext = () => {
    const itemsCount = items.length;
    if (currentItemIndex >= itemsCount - 1) {
      setCurrentItemIndex(0);
    } else {
      setCurrentItemIndex(prevIndex => prevIndex + 1);
    }
  };
  const currentItem = items[currentItemIndex];
  let previous = null;
  let next = null;
  let navAction = "";
  if (items.length > 1) {
    if (viewMode === "item") {
      navAction = /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(IconButton, {
          onClick: goToGrid,
          title: gridViewLabel,
          children: /*#__PURE__*/_jsx(GridView, {})
        }), /*#__PURE__*/_jsxs(Text4, {
          style: {
            paddingLeft: spacing8,
            paddingRight: spacing8
          },
          children: [currentItemIndex + 1, " / ", items.length]
        })]
      });
    } else {
      navAction = /*#__PURE__*/_jsx(NavButton, {
        onClick: goToItem,
        title: backLabel,
        children: /*#__PURE__*/_jsx(ArrowBack, {})
      });
    }
    previous = /*#__PURE__*/_jsx(NavButton, {
      onClick: handlePrevious,
      title: previousLabel,
      children: /*#__PURE__*/_jsx(ChevronLeft, {})
    });
    next = /*#__PURE__*/_jsx(NavButton, {
      onClick: handleNext,
      title: nextLabel,
      children: /*#__PURE__*/_jsx(ChevronRight, {})
    });
  }
  return /*#__PURE__*/_jsx(Mask, {
    active: true,
    type: "media",
    ...props,
    children: /*#__PURE__*/_jsx(AppearanceInvert, {
      inverted: true,
      style: {
        height: "100%"
      },
      children: /*#__PURE__*/_jsxs(Container, {
        children: [/*#__PURE__*/_jsx(Head, {
          start: /*#__PURE__*/_jsx(HeadPiece, {
            children: navAction
          }),
          end: /*#__PURE__*/_jsxs(HeadPiece, {
            children: [viewMode === "item" && /*#__PURE__*/_jsxs(HeadNav, {
              children: [previous, next]
            }), /*#__PURE__*/_jsx(IconButton, {
              onClick: handleClose,
              "data-testid": "closeButton",
              title: closeLabel,
              children: /*#__PURE__*/_jsx(Clear, {})
            })]
          })
        }), viewMode === "item" ? /*#__PURE__*/_jsx(ItemLayout, {
          previous: previous,
          next: next,
          main: currentItem,
          foot: /*#__PURE__*/_jsx(Caption, {
            title: currentItem.props.title,
            caption: currentItem.props.caption,
            attribution: currentItem.props.attribution
          })
        }) : /*#__PURE__*/_jsx(Grid, {
          items: items,
          activeIndex: currentItemIndex,
          onItemSelected: handleItemSelected
        }), closeKey !== null && /*#__PURE__*/_jsx(HotKey, {
          sequence: [closeKey],
          onMatch: handleClose
        })]
      })
    })
  });
};
MediaModal.propTypes = {
  /** Called when the close button is clicked */
  onClose: PropTypes.func.isRequired,
  /** Used to define what the media items in the modal are. Should be one or more of ModalImage and ModalVideo */
  children: PropTypes.node.isRequired,
  /** Defines which item in the gallery should be active when it first opens. */
  initialItemIndex: PropTypes.number,
  /** Defines which key, when pressed, will close the modal; a value of 'null' disables the feature */
  closeKey: PropTypes.string,
  /** Label for the "grid view" control */
  gridViewLabel: PropTypes.string,
  /** Label for the "back" control */
  backLabel: PropTypes.string,
  /** Label for the "previous" control */
  previousLabel: PropTypes.string,
  /** Label for the "next" control */
  nextLabel: PropTypes.string,
  /** Label for the "close" control */
  closeLabel: PropTypes.string
};
export default MediaModal;